/* =================================================================
   MyEbike Compare flow — 99bikes-style sticky bar + modal drawer
   -----------------------------------------------------------------
   - Persists selected products in sessionStorage (mb_compare_v1)
   - Renders <compare-bar> globally (shows when 1+ items selected)
   - Renders <compare-drawer> globally (modal, opens at 2+ items)
   - Wires every <input.mb-compare-cb> on the page so any product card
     across the site (collection, search, related, recently viewed,
     bestsellers, PDP) can add/remove a product
   ================================================================= */

(function () {
  'use strict';

  const STORAGE_KEY = 'mb_compare_v1';
  const MAX_SLOTS = 4;

  // ---------- Store --------------------------------------------------------
  const Store = {
    items: [],
    listeners: new Set(),

    load() {
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        if (Array.isArray(parsed)) {
          this.items = parsed.filter((it) => it && it.id);
        }
      } catch (e) {
        this.items = [];
      }
    },

    save() {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
      } catch (e) {
        /* sessionStorage might be disabled — fail open */
      }
    },

    has(id) {
      return this.items.some((it) => String(it.id) === String(id));
    },

    add(item) {
      if (this.has(item.id)) return { ok: true, full: false };
      if (this.items.length >= MAX_SLOTS) {
        return { ok: false, full: true };
      }
      this.items.push(item);
      this.save();
      this.emit();
      return { ok: true, full: false };
    },

    remove(id) {
      const before = this.items.length;
      this.items = this.items.filter((it) => String(it.id) !== String(id));
      if (this.items.length !== before) {
        this.save();
        this.emit();
      }
    },

    clear() {
      this.items = [];
      this.save();
      this.emit();
    },

    onChange(fn) {
      this.listeners.add(fn);
      return () => this.listeners.delete(fn);
    },

    emit() {
      this.listeners.forEach((fn) => {
        try {
          fn(this.items.slice());
        } catch (e) {
          /* swallow */
        }
      });
    },
  };

  Store.load();
  // Expose for debugging / PDP integration
  window.MyEbikeCompare = {
    add: (item) => Store.add(item),
    remove: (id) => Store.remove(id),
    clear: () => Store.clear(),
    has: (id) => Store.has(id),
    items: () => Store.items.slice(),
    MAX_SLOTS: MAX_SLOTS,
  };

  // ---------- Toast --------------------------------------------------------
  function toast(message) {
    let el = document.querySelector('.mb-compare-toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'mb-compare-toast';
      document.body.appendChild(el);
    }
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(el._t);
    el._t = setTimeout(() => el.classList.remove('is-visible'), 2800);
  }

  // ---------- Checkbox wiring ----------------------------------------------
  function readDataset(input) {
    const el = input.closest('[data-mb-compare]') || input;
    const ds = el.dataset || {};
    const d = input.dataset || {};
    // Specs are pre-composed display strings emitted by
    // snippets/mb-compare-checkbox.liquid from the post-#124 metafield set
    // (descriptors.subtitle, custom.summary, specs.motor_w, etc.). Storing
    // them on the checkbox avoids a per-product .js fetch on drawer open
    // and — critically — skips the AU >250W disclaimer banner that sits at
    // the top of description_html on 16 of the 44 ebikes.
    return {
      id: input.value || ds.productId,
      title: d.title || ds.productTitle || '',
      url: d.url || ds.productUrl || '',
      image: d.image || ds.productImage || '',
      priceText: d.price || ds.productPrice || '',
      compareAtText: d.compare || ds.productCompare || '',
      vendor: d.vendor || ds.productVendor || '',
      specs: {
        motor: d.specMotor || '',
        battery: d.specBattery || '',
        range: d.specRange || '',
        weight: d.specWeight || '',
        frame: d.specFrame || '',
        gears: d.specGears || '',
        brakes: d.specBrakes || '',
      },
    };
  }

  function syncCheckboxes() {
    const ids = new Set(Store.items.map((it) => String(it.id)));
    document.querySelectorAll('input.mb-compare-cb').forEach((cb) => {
      const id = String(cb.value || (cb.dataset && cb.dataset.productId) || '');
      const wrap = cb.closest('.mb-compare-toggle');
      const checked = ids.has(id);
      if (cb.checked !== checked) cb.checked = checked;
      if (wrap) wrap.classList.toggle('is-checked', checked);
    });
  }

  function handleChange(e) {
    const cb = e.target;
    if (!(cb instanceof HTMLInputElement) || !cb.classList.contains('mb-compare-cb')) return;
    const data = readDataset(cb);
    if (!data.id) return;

    if (cb.checked) {
      const result = Store.add(data);
      if (!result.ok && result.full) {
        cb.checked = false;
        toast('Compare up to ' + MAX_SLOTS + ' bikes at a time. Remove one to add another.');
        return;
      }
    } else {
      Store.remove(data.id);
    }
    syncCheckboxes();
  }

  document.addEventListener('change', handleChange);
  // Also wire clicks on the visible label so toggle stays snappy without a checkbox refresh delay
  document.addEventListener('DOMContentLoaded', syncCheckboxes);
  // After Shopify section renders / partial reloads
  document.addEventListener('shopify:section:load', syncCheckboxes);

  Store.onChange(() => syncCheckboxes());

  // ---------- <compare-bar> ------------------------------------------------
  class CompareBar extends HTMLElement {
    constructor() {
      super();
      this._unsub = null;
      this._collapsed = false; // mobile thumbnail expanded state
    }
    connectedCallback() {
      this.render();
      // Publish height on every store change — items added/removed change
      // the bar's height (slots row toggles on mobile). render() runs first;
      // we publish after layout settles in a microtask.
      this._unsub = Store.onChange(() => {
        this.render();
        requestAnimationFrame(() => this.publishHeight());
      });
      this.addEventListener('click', this.onClick);
      // Visual-viewport tracking: position:fixed anchors to the LAYOUT viewport,
      // which on mobile extends behind a shown URL bar. The visual viewport is
      // what the user actually sees. We push the bar up by (layout - visual) at
      // the bottom edge so it's always flush with the screen bottom regardless
      // of URL bar state. This survives URL-bar collapse/expand on Chrome and
      // Safari mobile (dvh isn't reliable enough across versions).
      this._scheduleViewportUpdate = () => {
        if (this._vvRaf) return;
        this._vvRaf = requestAnimationFrame(() => {
          this._vvRaf = null;
          this.updateViewportOffset();
          // Width changes (orientation, resize) re-flow the bar — slots may
          // wrap or unwrap, changing height. Republish so PDP sticky ATC
          // tracks the new gap.
          this.publishHeight();
        });
      };
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this._scheduleViewportUpdate);
        window.visualViewport.addEventListener('scroll', this._scheduleViewportUpdate);
      }
      window.addEventListener('resize', this._scheduleViewportUpdate);
      this.updateViewportOffset();
      // Initial publish after first render
      requestAnimationFrame(() => this.publishHeight());
    }
    disconnectedCallback() {
      if (this._unsub) this._unsub();
      this.removeEventListener('click', this.onClick);
      if (window.visualViewport && this._scheduleViewportUpdate) {
        window.visualViewport.removeEventListener('resize', this._scheduleViewportUpdate);
        window.visualViewport.removeEventListener('scroll', this._scheduleViewportUpdate);
      }
      if (this._scheduleViewportUpdate) {
        window.removeEventListener('resize', this._scheduleViewportUpdate);
      }
      if (this._vvRaf) cancelAnimationFrame(this._vvRaf);
      // Clean up our published bar-height vars + body class so other sticky
      // bottom UIs sit flush again when bar element is removed.
      document.documentElement.style.removeProperty('--mb-cbar-h');
      document.documentElement.classList.remove('mb-has-compare-bar');
    }
    updateViewportOffset() {
      const vv = window.visualViewport;
      if (!vv) {
        this.style.setProperty('--mb-cbar-vv-offset', '0px');
        return;
      }
      // How much of the layout viewport sits BELOW the visual viewport bottom?
      // That's the area covered by the URL bar (or virtual keyboard). Push the
      // bar up by exactly that much. Math.max(0, ...) guards against rounding
      // jitter giving negative values.
      const overlap = Math.max(0, window.innerHeight - (vv.offsetTop + vv.height));
      this.style.setProperty('--mb-cbar-vv-offset', overlap + 'px');
    }
    // Publish bar visibility + height to the document so other sticky
    // bottom elements (e.g. PDP sticky ATC) can stack above us instead of
    // being hidden underneath. We use a CSS variable + body class so any
    // section can opt in via CSS without coupling JS modules.
    publishHeight() {
      const root = document.documentElement;
      if (this.classList.contains('is-visible')) {
        const h = this.offsetHeight || 0;
        root.style.setProperty('--mb-cbar-h', h + 'px');
        root.classList.add('mb-has-compare-bar');
      } else {
        root.style.removeProperty('--mb-cbar-h');
        root.classList.remove('mb-has-compare-bar');
      }
    }
    onClick = (e) => {
      const removeBtn = e.target.closest('[data-mb-bar-remove]');
      if (removeBtn) {
        e.preventDefault();
        Store.remove(removeBtn.getAttribute('data-mb-bar-remove'));
        return;
      }
      if (e.target.closest('[data-mb-bar-clear]')) {
        e.preventDefault();
        Store.clear();
        return;
      }
      if (e.target.closest('[data-mb-bar-compare]')) {
        e.preventDefault();
        if (Store.items.length < 2) return;
        const drawer = document.querySelector('compare-drawer');
        if (drawer) drawer.open();
        return;
      }
      if (e.target.closest('[data-mb-bar-toggle]')) {
        e.preventDefault();
        this._collapsed = !this._collapsed;
        this.classList.toggle('is-collapsed', this._collapsed);
        const toggleBtn = this.querySelector('[data-mb-bar-toggle]');
        if (toggleBtn) {
          toggleBtn.setAttribute('aria-expanded', String(!this._collapsed));
          toggleBtn.setAttribute('aria-label', this._collapsed ? 'Show selected bikes' : 'Hide selected bikes');
        }
        // Collapse animation runs ~0.28s; republish height on transition end
        // so PDP sticky ATC tracks the new gap. We listen once.
        const onEnd = () => { this.publishHeight(); };
        const slots = this.querySelector('.mb-cbar__slots');
        if (slots) {
          slots.addEventListener('transitionend', onEnd, { once: true });
          // Safety: also republish after expected duration in case the event
          // doesn't fire (e.g. transition cancelled by next interaction).
          setTimeout(onEnd, 320);
        } else {
          onEnd();
        }
        return;
      }
    };
    render() {
      const items = Store.items;
      const visible = items.length > 0;
      this.classList.toggle('is-visible', visible);

      const slots = [];
      for (let i = 0; i < MAX_SLOTS; i++) {
        const it = items[i];
        if (it) {
          slots.push(
            `<div class="mb-cbar__slot mb-cbar__slot--filled" data-id="${escapeAttr(it.id)}">
              <a class="mb-cbar__thumb" href="${escapeAttr(it.url || '#')}" aria-label="${escapeAttr(it.title)}">
                ${
                  it.image
                    ? `<img src="${escapeAttr(it.image)}" alt="${escapeAttr(it.title)}" loading="lazy">`
                    : `<span class="mb-cbar__thumb-ph" aria-hidden="true">${escapeHtml(initials(it.title))}</span>`
                }
              </a>
              <button class="mb-cbar__remove" type="button" data-mb-bar-remove="${escapeAttr(it.id)}" aria-label="Remove ${escapeAttr(it.title)} from compare">
                <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
              </button>
              <span class="mb-cbar__slot-title" title="${escapeAttr(it.title)}">${escapeHtml(truncate(it.title, 40))}</span>
            </div>`
          );
        } else {
          slots.push(
            `<div class="mb-cbar__slot mb-cbar__slot--empty" aria-hidden="true">
              <span class="mb-cbar__plus">+</span>
            </div>`
          );
        }
      }

      const compareDisabled = items.length < 2;
      // Disabled state used to read just "Compare" with no number, which
      // looked broken (grey-on-grey, no affordance for what to do next).
      // "Add 1 more" makes the gating action explicit.
      const compareLabel = compareDisabled
        ? (items.length === 0 ? 'Add 2 to compare' : 'Add 1 more')
        : `Compare (${items.length})`;

      this.innerHTML = `
        <div class="mb-cbar__inner mb-wrap">
          <div class="mb-cbar__counter">
            Compare <span class="mb-cbar__count">(${items.length}<span class="mb-cbar__max">/${MAX_SLOTS}</span>)</span>
          </div>
          <button type="button" class="mb-cbar__mobile-toggle" data-mb-bar-toggle aria-expanded="${this._collapsed ? 'false' : 'true'}" aria-label="${this._collapsed ? 'Show' : 'Hide'} selected bikes">
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M3 9l4-4 4 4" stroke="currentColor" stroke-width="1.8" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
          <div class="mb-cbar__slots" role="list">
            ${slots.join('')}
          </div>
          <div class="mb-cbar__actions">
            <button
              type="button"
              class="mb-cbar__btn mb-cbar__btn--primary"
              data-mb-bar-compare
              ${compareDisabled ? 'disabled aria-disabled="true"' : ''}
            >${compareLabel}</button>
            <button type="button" class="mb-cbar__btn mb-cbar__btn--ghost" data-mb-bar-clear>Clear all</button>
          </div>
        </div>
      `;
    }
  }

  // ---------- <compare-drawer> ---------------------------------------------
  class CompareDrawer extends HTMLElement {
    constructor() {
      super();
      this._open = false;
      this._unsub = null;
      this._onKey = (e) => {
        if (e.key === 'Escape' && this._open) this.close();
      };
    }
    connectedCallback() {
      this.render();
      this._unsub = Store.onChange(() => {
        if (this._open && Store.items.length < 2) this.close();
        else if (this._open) this.render();
      });
      this.addEventListener('click', this.onClick);
      document.addEventListener('keydown', this._onKey);
    }
    disconnectedCallback() {
      if (this._unsub) this._unsub();
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('keydown', this._onKey);
    }
    open() {
      this._open = true;
      this.classList.add('is-open');
      document.documentElement.classList.add('mb-compare-no-scroll');
      this.render();
    }
    close() {
      this._open = false;
      this.classList.remove('is-open');
      document.documentElement.classList.remove('mb-compare-no-scroll');
    }
    onClick = (e) => {
      if (e.target.closest('[data-mb-drawer-close]')) {
        e.preventDefault();
        this.close();
        return;
      }
      if (e.target === this || e.target.classList.contains('mb-cdr__overlay')) {
        this.close();
        return;
      }
      const removeBtn = e.target.closest('[data-mb-drawer-remove]');
      if (removeBtn) {
        e.preventDefault();
        Store.remove(removeBtn.getAttribute('data-mb-drawer-remove'));
        return;
      }
    };
    render() {
      const items = Store.items;
      const cols = items
        .map(
          (it) => `
          <th class="mb-cdr__col-head" scope="col">
            <div class="mb-cdr__col-thumb">
              ${
                it.image
                  ? `<img src="${escapeAttr(it.image)}" alt="${escapeAttr(it.title)}" loading="lazy">`
                  : `<span class="mb-cdr__col-thumb-ph">${escapeHtml(initials(it.title))}</span>`
              }
              <button type="button" class="mb-cdr__col-remove" data-mb-drawer-remove="${escapeAttr(it.id)}" aria-label="Remove ${escapeAttr(it.title)} from compare">
                <svg width="11" height="11" viewBox="0 0 10 10" aria-hidden="true"><path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
              </button>
            </div>
            ${
              it.vendor
                ? `<div class="mb-cdr__col-vendor">${escapeHtml(it.vendor)}</div>`
                : ''
            }
            <a class="mb-cdr__col-title" href="${escapeAttr(it.url || '#')}">${escapeHtml(it.title)}</a>
            <a class="mb-cdr__col-cta" href="${escapeAttr(it.url || '#')}">View details</a>
          </th>`
        )
        .join('');

      // Pre-fetch attributes — drawer renders cells using data we
      // captured at checkbox-select time, so it works without re-fetching products.
      // For richer comparison data (specs metafields), we lazy-fetch on open
      // via Shopify's product JSON endpoints.
      this.innerHTML = `
        <div class="mb-cdr__overlay" aria-hidden="true"></div>
        <div class="mb-cdr__panel" role="dialog" aria-modal="true" aria-label="Product comparison">
          <div class="mb-cdr__head">
            <h3 class="mb-cdr__title">Product Comparison</h3>
            <button type="button" class="mb-cdr__close" data-mb-drawer-close aria-label="Close comparison">
              <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M2 2l10 10M12 2L2 12" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
            </button>
          </div>
          <div class="mb-cdr__scroll">
            <table class="mb-cdr__table">
              <thead>
                <tr>
                  <th class="mb-cdr__row-label" scope="col"><span class="visually-hidden">Spec</span></th>
                  ${cols}
                </tr>
              </thead>
              <tbody data-mb-drawer-body>
                ${this.renderRow('Price', items.map((it) => priceCell(it)))}
                ${this.renderRow('Motor', items.map((it) => specCell(it, 'motor')), 'is-hydrating')}
                ${this.renderRow('Battery', items.map((it) => specCell(it, 'battery')), 'is-hydrating')}
                ${this.renderRow('Range', items.map((it) => specCell(it, 'range')), 'is-hydrating')}
                ${this.renderRow('Weight', items.map((it) => specCell(it, 'weight')), 'is-hydrating')}
                ${this.renderRow('Frame', items.map((it) => specCell(it, 'frame')), 'is-hydrating')}
                ${this.renderRow('Gears', items.map((it) => specCell(it, 'gears')), 'is-hydrating')}
                ${this.renderRow('Brakes', items.map((it) => specCell(it, 'brakes')), 'is-hydrating')}
              </tbody>
            </table>
          </div>
        </div>
      `;
      // Lazy-load richer specs from product .json endpoints
      if (this._open) this.hydrateSpecs(items);
    }
    renderRow(label, cells, cls) {
      return `<tr class="${cls || ''}">
        <td class="mb-cdr__row-label">${escapeHtml(label)}</td>
        ${cells.map((c) => `<td class="mb-cdr__cell">${c}</td>`).join('')}
      </tr>`;
    }
    hydrateSpecs(items) {
      const body = this.querySelector('[data-mb-drawer-body]');
      if (!body) return;
      // Row order matches the tbody render above. Price is row 0,
      // specs at rows 1..7.
      const rowMap = {
        motor: 1,
        battery: 2,
        range: 3,
        weight: 4,
        frame: 5,
        gears: 6,
        brakes: 7,
      };

      // Fast path — every item already has spec data on its dataset
      // (snippets/mb-compare-checkbox.liquid emits these from metafields).
      // Only fetch product .js when the dataset is empty for at least one
      // tracked key, which happens for non-metafield-backed products
      // (rentals, accessories, legacy products).
      const needsFetch = items.map((it) => {
        const s = (it && it.specs) || {};
        const hasAny = s.motor || s.battery || s.range || s.weight || s.frame || s.gears || s.brakes;
        return !hasAny;
      });

      // Drop loading state on rows that won't change (dataset-only items)
      Object.keys(rowMap).forEach((key) => {
        const tr = body.children[rowMap[key]];
        if (tr) tr.classList.remove('is-hydrating');
      });

      const anyToFetch = needsFetch.some(Boolean);
      if (!anyToFetch) return;

      Promise.all(
        items.map((it, i) =>
          needsFetch[i] && it.url
            ? fetch(it.url + '.js', { credentials: 'same-origin' })
                .then((r) => (r.ok ? r.json() : null))
                .catch(() => null)
            : Promise.resolve(null)
        )
      ).then((products) => {
        Object.keys(rowMap).forEach((key) => {
          const rowIdx = rowMap[key];
          const tr = body.children[rowIdx];
          if (!tr) return;
          const tds = tr.querySelectorAll('td.mb-cdr__cell');
          products.forEach((p, i) => {
            if (!needsFetch[i] || !p) return;
            const td = tds[i];
            if (!td) return;
            const value = extractSpec(p, key);
            if (value) td.innerHTML = value;
          });
        });
      });
    }
  }

  function priceCell(item) {
    const p = item.priceText || '';
    if (!p) return '<span class="mb-cdr__cell-muted">—</span>';
    const compare = item.compareAtText || '';
    if (compare) {
      return `<div class="mb-cdr__price">
        <span class="mb-cdr__price-now">${escapeHtml(p)}</span>
        <span class="mb-cdr__price-was">${escapeHtml(compare)}</span>
      </div>`;
    }
    return `<span class="mb-cdr__price-now">${escapeHtml(p)}</span>`;
  }

  function specCell(item, key) {
    const v = item && item.specs ? item.specs[key] : '';
    if (!v) return '<span class="mb-cdr__cell-muted">—</span>';
    return escapeHtml(v);
  }

  // Fallback hydration from /products/<handle>.js — only fires for products
  // whose checkbox didn't carry pre-composed spec data (e.g. accessories
  // that someone added a compare checkbox to without populating metafields).
  // Never called for the post-#124 ebike catalogue since every imported
  // product has the metafields.
  function extractSpec(product, key) {
    if (!product) return null;
    // Last-ditch tag-prefix lookup for non-catalogue products that opt in
    // to compare. The post-#124 ebike catalogue holds these in metafields
    // (specs.motor_w, specs.battery_wh, etc.) which the .js endpoint
    // doesn't expose — that's why the snippet emits them on the checkbox.
    const tagPrefixMap = {
      motor: ['motor:', 'motor_type:'],
      battery: ['battery:', 'battery_capacity:'],
      range: ['range:', 'range_km:'],
      weight: ['weight:', 'weight_kg:'],
      frame: ['frame_type:', 'frame:', 'frame_material:'],
      gears: ['gears:', 'gears_count:'],
      brakes: ['brakes:'],
    };
    const prefixes = tagPrefixMap[key];
    if (prefixes && Array.isArray(product.tags)) {
      for (const tag of product.tags) {
        for (const p of prefixes) {
          if (typeof tag === 'string' && tag.toLowerCase().indexOf(p) === 0) {
            return escapeHtml(tag.slice(p.length).trim());
          }
        }
      }
    }
    return null;
  }

  // ---------- Helpers ------------------------------------------------------
  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
  function escapeAttr(s) {
    return escapeHtml(s);
  }
  function truncate(s, n) {
    s = String(s || '');
    return s.length > n ? s.slice(0, n - 1).trimEnd() + '…' : s;
  }
  function initials(s) {
    return String(s || '?')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join('')
      .toUpperCase();
  }

  // ---------- Register custom elements ------------------------------------
  if (!customElements.get('compare-bar')) customElements.define('compare-bar', CompareBar);
  if (!customElements.get('compare-drawer')) customElements.define('compare-drawer', CompareDrawer);
})();
